import { useState, useEffect, useCallback } from 'react';
import { navItems } from '@/mocks/homeData';
import HeroSection from './components/HeroSection';
import AppSection from './components/AppSection';
import IndexSection from './components/IndexSection';
import CarouselSection from './components/CarouselSection';
import UpdatesSection from './components/UpdatesSection';
import CardsSection from './components/CardsSection';
import AboutSection from './components/AboutSection';
import MediaSection from './components/MediaSection';
import CommentsSection from './components/CommentsSection';
import TopicsSection from './components/TopicsSection';
import RankingSection from './components/RankingSection';
import ContactSection from './components/ContactSection';
import FeaturesSection from './components/FeaturesSection';
import FaqSection from './components/FaqSection';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleNavClick = () => {
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* ===== Header ===== */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-[64px] md:h-[72px]">
            {/* Logo */}
            <a
              href="#hero"
              className={`flex items-center gap-2 font-heading font-black text-xl md:text-2xl transition-colors whitespace-nowrap ${
                scrolled ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              <span
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                  scrolled ? 'bg-primary-500 text-background-50' : 'bg-primary-500 text-background-50'
                }`}
              >
                黑
              </span>
              每日黑料
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={item.anchor}
                  className={`whitespace-nowrap px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    scrolled
                      ? 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                      : 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Mobile hamburger */}
            <button
              className={`md:hidden w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                scrolled
                  ? 'text-foreground-700 hover:bg-background-100'
                  : 'text-background-50 hover:bg-background-50/10'
              }`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="打开菜单"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/60">
            <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={item.anchor}
                  onClick={handleNavClick}
                  className="whitespace-nowrap px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-all"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* ===== Main Content ===== */}
      <main>
        <HeroSection />
        <AppSection />
        <IndexSection />
        <CarouselSection />
        <UpdatesSection />
        <CardsSection />
        <AboutSection />
        <MediaSection />
        <CommentsSection />
        <TopicsSection />
        <RankingSection />
        <ContactSection />
        <FeaturesSection />
        <FaqSection />
      </main>

      {/* ===== Footer ===== */}
      <footer id="footer" className="bg-background-950 text-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            {/* 品牌简介 */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center font-heading font-black text-sm">
                  黑
                </span>
                <span className="font-heading font-black text-xl">每日黑料</span>
              </div>
              <p className="text-foreground-400 text-sm leading-relaxed mb-4">
                每日黑料吃瓜网 — 全网领先的黑料爆料与吃瓜资讯聚合平台。坚持「有料必报、去伪存真」，为千万吃瓜爱好者提供最新鲜、最真实的热门黑料资讯。
              </p>
              <div className="flex items-center gap-3">
                <a href="#" className="w-9 h-9 rounded-lg bg-background-50/10 hover:bg-primary-500 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-background-50/70 text-sm"></i>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg bg-background-50/10 hover:bg-primary-500 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-background-50/70 text-sm"></i>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg bg-background-50/10 hover:bg-primary-500 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-bilibili-line text-background-50/70 text-sm"></i>
                </a>
                <a href="#" className="w-9 h-9 rounded-lg bg-background-50/10 hover:bg-primary-500 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-music-line text-background-50/70 text-sm"></i>
                </a>
              </div>
            </div>

            {/* 快速导航 */}
            <div>
              <h4 className="font-heading font-bold text-sm mb-4 text-background-50">
                <a href="#hero" className="hover:text-primary-400 transition-colors">快速导航</a>
              </h4>
              <ul className="space-y-2.5">
                {navItems.slice(1).map((item) => (
                  <li key={item.id}>
                    <a
                      href={item.anchor}
                      className="text-foreground-400 hover:text-primary-400 text-sm transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 联系我们 */}
            <div>
              <h4 className="font-heading font-bold text-sm mb-4 text-background-50">
                <a href="#contact" className="hover:text-primary-400 transition-colors">联系我们</a>
              </h4>
              <ul className="space-y-2.5 text-sm text-foreground-400">
                <li>投稿邮箱：tips@meiriheiliao.com</li>
                <li>商务合作：biz@meiriheiliao.com</li>
                <li>微信客服：MRHL_kefu</li>
                <li>用户反馈：<a href="#contact" className="text-primary-400 hover:underline">在线提交</a></li>
              </ul>
            </div>

            {/* 合作伙伴 */}
            <div>
              <h4 className="font-heading font-bold text-sm mb-4 text-background-50">合作伙伴</h4>
              <p className="text-foreground-500 text-sm leading-relaxed">
                诚邀各类优质内容平台、媒体机构、品牌方加入合作生态。合作洽谈请通过上方联系方式与我们取得联系。
              </p>
            </div>
          </div>

          {/* 版权信息 */}
          <div className="pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-foreground-500 text-xs">
              &copy; {new Date().getFullYear()} 每日黑料吃瓜网 meiriheiliao.com. 本站内容仅供吃瓜娱乐，请勿对号入座。
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <a href="#" className="hover:text-foreground-300 transition-colors">用户协议</a>
              <a href="#" className="hover:text-foreground-300 transition-colors">隐私政策</a>
              <a href="#" className="hover:text-foreground-300 transition-colors">免责声明</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}